import { FC } from 'react';
import { Router } from 'react-router-dom';
import history from 'packages/lib/utils/history';
import { ConfigProvider, Modal, App as AntdApp } from 'antd';
import Routes from 'packages/main/routes/app-routes';
import * as Sentry from '@sentry/react';
import { RecoilRoot } from 'recoil';
import { SWRConfig, SWRConfiguration } from 'swr';
import 'packages/main/styles/base.scss';
import 'packages/main/styles/common.scss';
import 'material-icons/iconfont/material-icons.css'; // Usage: https://github.com/marella/material-icons
import { captureException } from '@sentry/react';
import { ResponseNotOk } from 'packages/lib/utils/error';
import { redirectToLogin } from 'packages/lib/auth';
import OktaSecurity from 'packages/main/components/security';
import { customDefaultTheme } from 'packages/lib/common-ui/theme';
import { AntdGlobal } from 'packages/lib/common-ui/notification/notify';
import { CacheProvider } from '@emotion/react';
import { TssCacheProvider } from 'tss-react';
import createCache from '@emotion/cache';
import { useServiceLevelMetrics } from 'packages/lib/metrics/sla-metrics';
const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const tssCache = createCache({
  key: 'tss',
});

// Antd confirm modal dialogs will stick around even if user changes pages. The official docs suggest
// clearing them on history change, as done below. See near bottom of docs here: https://ant.design/components/modal/
history.listen((event) => {
  Modal.destroyAll();
});

const swrGlobalConfig: Partial<SWRConfiguration> = {
  errorRetryCount: 1,
  suspense: true,
  dedupingInterval: 5_000,
  errorRetryInterval: 10_000,
  focusThrottleInterval: 60_000,
  onError: (err, key, config) => {
    if (err instanceof ResponseNotOk && err.response.status === 401) {
      void redirectToLogin();
      return;
    }
    captureException(err);
  },
};

const App: FC<React.PropsWithChildren<unknown>> = () => {
  useServiceLevelMetrics();
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope): void => {
        scope.setTag('location', 'App root');
      }}
      fallback={
        <p>
          An error occurred we could not recover from. The error has been
          logged. If the issue persists please notify #annotation-tools
        </p>
      }
    >
      <RecoilRoot>
        <CacheProvider value={muiCache}>
          <TssCacheProvider value={tssCache}>
            <Router history={history}>
              <OktaSecurity>
                <SWRConfig value={swrGlobalConfig}>
                  <ConfigProvider theme={customDefaultTheme}>
                    {/* component={false} disables styles (no .ant-app div wrapper) */}
                    <AntdApp component={false}>
                      <AntdGlobal />
                      <Routes />
                    </AntdApp>
                  </ConfigProvider>
                </SWRConfig>
              </OktaSecurity>
            </Router>
          </TssCacheProvider>
        </CacheProvider>
      </RecoilRoot>
    </Sentry.ErrorBoundary>
  );
};

export default App;
